import React, {
  ChangeEventHandler,
  FormEventHandler,
  useMemo,
  useState,
} from 'react'
import { CustomerButton } from './CustomerButton'
import { isEditor } from '../utils/misc'
import { useMargin } from '../utils/hooks'
import {
  ContactFormPayload,
  DynamicContentType,
  FormData,
  FormField,
} from '@bm-js/atlascloud-shared'

type ChangeHandler = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => void

type Props = {
  content: DynamicContentType<FormData>
  loading: boolean
  error: boolean
  success: boolean
  handleSubmit: (data: ContactFormPayload) => void
}

type WrapperProps = FormField & {
  id: string
  value: string
  onChange: ChangeHandler
  disabled: boolean
  uuid: string
}

type InputProps = Omit<FormField, 'width'> & {
  className: string
  onChange: ChangeHandler
}

export const FormContent = ({
  content,
  handleSubmit,
  loading,
  error,
  success,
}: Props) => {
  const getInitialValues = () => {
    const initialValues: Record<string, string> = {}
    content.data.fields.forEach((field) => {
      initialValues[field.name] = ''
    })
    return initialValues
  }

  const [formData, setFormData] = useState(getInitialValues())

  const handleChange: ChangeHandler = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const margin = useMargin(content)

  const onSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    if (success) return
    handleSubmit({
      formData,
      formUuid: content.uuid,
    })
  }

  if (!content.data.contactEmail) {
    if (isEditor()) {
      return (
        <p className='mx-auto max-w-md justify-self-center bg-red-600 p-5 text-center text-white'>
          För att kontaktformuläret ska visas på hemsidan måste du fylla i
          'Kontaktmail'. Utan 'Kontaktmail' kommer inte formuläret att fungera.
        </p>
      )
    }
    return null
  }

  return (
    <div className='@container'>
      <form
        onSubmit={onSubmit}
        className={`grid grid-cols-1 gap-x-20 gap-y-14 rounded-2xl border border-neutral-300 px-10 pb-12 pt-16 @2xl:grid-cols-2 ${margin}`}
      >
        {content.data.fields.map((field) => {
          const id = `${content.uuid}_${field.name}`
          return (
            <FormContenInputtWrapper
              id={id}
              required={field.required}
              width={field.width}
              type={field.type}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              uuid={content.uuid}
              key={`form__${id}`}
              disabled={loading}
            />
          )
        })}
        <div className='col-span-1 flex justify-center @2xl:col-span-2'>
          {success ? (
            <p>{content.data.successText}</p>
          ) : (
            <div className='text-center'>
              <CustomerButton
                className='w-40'
                btnColor='dark'
                btnType='outline'
                type='submit'
                disabled={loading}
              >
                {loading ? 'Skickar...' : content.data.submitText}
              </CustomerButton>
              {!!error && (
                <p className='mt-4 max-w-sm'>
                  Något gick fel när formuläret skulle skickas. Försök igen
                  eller kontakta oss istället på {content.data.contactEmail}
                </p>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

const FormContenInputtWrapper = (field: WrapperProps) => {
  const { width, ...fieldProps } = field

  const widthClass = useMemo(() => {
    switch (width) {
      case 'half':
        return 'col-span-1'
      case 'full':
        return 'col-span-1 @2xl:col-span-2'
      default:
        return 'col-span-1 @2xl:col-span-2'
    }
  }, [width])

  return (
    <div className={`${widthClass} `}>
      <label htmlFor={field.id} className='block text-neutral-500'>
        {field.name}
        {!!field.required && '*'}
      </label>
      <FormContentInput
        {...fieldProps}
        className={`block w-full resize-none border-b border-neutral-300 bg-transparent text-base outline-none`}
      />
    </div>
  )
}

const FormContentInput = (field: InputProps) => {
  switch (field.type) {
    case 'textarea':
      return <TextArea {...field} />
    default:
      return <input {...field} />
  }
}

const TextArea = (field: InputProps) => {
  const { onChange, ...textareaProps } = field
  const MIN_LINES = 2
  const [lines, setLines] = useState(MIN_LINES)

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    onChange(e)
    const rows = e.target.value.split('\n').length
    setLines(Math.max(rows, MIN_LINES))
  }

  return (
    <textarea
      {...textareaProps}
      className={`${field.className} hide-scrollbar`}
      style={{ height: lines * 1.5 + 'rem' }}
      onChange={handleChange}
    />
  )
}
